/**
 * The data behind the ranked ask zone, owned by the PAGE.
 *
 * Owner, 2026-09-08: *"lift those five fetches up so the count is right."*
 *
 * Each ask card used to fetch for itself and return null when it had nothing.
 * That made "N to answer" a guess: a collapsed zone never mounts its children,
 * so nothing had fetched yet, and the header counted asks that would turn out
 * to be empty the moment you opened it. Two of the five were already known to
 * the page (the prepared-day snapshot, and the release offer that arrives on
 * today-view); these are the other three.
 *
 * Each hook returns `count` — `null` while it is still unknown. `null` is NOT
 * zero: an ask that has not answered yet must not be hidden or counted out,
 * only left undecided until it says.
 */
import { useCallback, useEffect, useState } from 'react'

import {
  listProposals,
  type AdmissionProposal,
} from '../services/admissionService'
import { getStretch, type Stretch } from '../services/stretchService'
import {
  getDecisionGaps,
  getTodayRelease,
  type DecisionGapItem,
  type TodayReleaseResponse,
} from '../services/prioritizationService'

export interface Ask<T> {
  data: T
  /** How many things this ask has to show. `null` = not known yet. */
  count: number | null
  refresh: () => Promise<void>
}

function useAsk<T>(
  load: () => Promise<T>, empty: T, size: (v: T) => number, deps: unknown[] = [],
): Ask<T> {
  const [data, setData] = useState<T>(empty)
  const [count, setCount] = useState<number | null>(null)

  const refresh = useCallback(async () => {
    try {
      const v = await load()
      setData(v)
      setCount(size(v))
    } catch {
      // A failed load is not an empty one. Leaving `count` as it was keeps a
      // transient error from silently emptying the zone.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => { void refresh() }, [refresh])
  return { data, count, refresh }
}

/** Open admission proposals: decompose offers, trade-offs, missing details. */
export function useAdmissionProposals(): Ask<AdmissionProposal[]> {
  return useAsk<AdmissionProposal[]>(listProposals, [], (v) => v.length)
}

/** Tier-1 decision gaps. The card asks ONE at a time; the count is of items. */
export function useDecisionGaps(): Ask<DecisionGapItem[]> {
  return useAsk<DecisionGapItem[]>(getDecisionGaps, [], (v) => v.length)
}

/**
 * Today's release proposal.
 *
 * `skip` mirrors the card's own rule: while the curtain owns the question this
 * proposal is structurally forbidden to render, so asking the release engine
 * is a wasted call. Skipping resolves the count to 0 rather than leaving it
 * unknown — "we will not show this" is a decided answer, not a pending one.
 */
export function useTodaysRelease(skip: boolean): Ask<TodayReleaseResponse | null> {
  return useAsk<TodayReleaseResponse | null>(
    async () => (skip ? null : await getTodayRelease()),
    null,
    (v) => {
      if (!v || !v.release_live || !v.decision?.item_id) return 0
      const o = v.decision.resolved_outcome
      // A set-aside answer still renders a one-line bring-back; only a closed
      // outcome means there is nothing to show.
      const setAside = o === 'skipped' || o === 'rescheduled'
      return o && !setAside ? 0 : 1
    },
    [skip],
  )
}


/**
 * Today's optional slots and what could fill them.
 *
 * Owner, 2026-09-10: *"wire the optional_units and the richer stretch pool."*
 * `count` is the number of CANDIDATES (so the section hides when there are
 * none), while the rung's own `optional_units` rides in `data` for the copy.
 */
export function useStretch(): Ask<Stretch> {
  return useAsk<Stretch>(
    getStretch,
    { mode: 'average', core_units: 3, optional_units: 0, candidates: [] },
    (v) => v.candidates.length,
  )
}
